import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import { Cell, Pie, PieChart, ResponsiveContainer, Sector } from "recharts";
import company from "../../../assets/image/icons/companies.svg";
import branches from "../../../assets/image/icons/branches.svg";
import overdue from "../../../assets/image/icons/overdue.svg";
import compliances from "../../../assets/image/icons/compliances.svg";
import due from "../../../assets/image/icons/due.svg";
import hdfcIcon from "../../../assets/image/icons/hdfc.svg";
import ltIcon from "../../../assets/image/icons/l&t.svg";
import highRisk from "../../../assets/image/icons/high-risk.svg";
import mediumRisk from "../../../assets/image/icons/medium-risk.svg";
import lowRisk from "../../../assets/image/icons/low-risk.svg";
import celebrateGif from "../../../assets/celebrate.gif";
import moment from "moment/moment";
import { toast } from "react-toastify";
import { requestApi } from "../../../request-api";
import Dropdown from "../../layouts/dropdown/Dropdown";
import ExportDropdown from "../../layouts/dropdown/ExportDropdown";
import CalenderDropdown from "../../layouts/dropdown/CalenderDropdown";
import * as XLSX from "xlsx";
import * as XlsxPopulate from "xlsx-populate/browser/xlsx-populate";
import { useNavigate } from "react-router-dom";
import {
  filterCompliancesByDays,
  flattenDashboardCompliances,
  filterReportTreeByGlobalFilters,
  normalizeComplianceStatus,
  sumBarChartComplianceStatuses,
} from "../../../helpers/complianceDashboard";

const riskStatisticsLegend = [
  { id: "complied", label: "Complied", colorClass: "complied" },
  { id: "partial", label: "Partial Complied", colorClass: "partial" },
  { id: "notApplicable", label: "Not Applicable", colorClass: "not-applicable" },
  { id: "nonComplied", label: "Non Complied", colorClass: "non-complied" },
];

const complianceCategoriesLegend = [
  { id: "complied", label: "Complied", colorClass: "complied" },
  { id: "partial", label: "Partial Complied", colorClass: "partial" },
  { id: "notApplicable", label: "Not Applicable", colorClass: "not-applicable" },
  { id: "nonComplied", label: "Non Complied", colorClass: "non-complied" },
];

const NewDashbord = () => {
  const navigate = useNavigate();
  const [activeUpcomingId, setActiveUpcomingId] = useState(null);
  const [activePieIndex, setActivePieIndex] = useState(null);

  const renderActivePieSlice = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 6}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          cornerRadius={6}
        />
      </g>
    );
  };

  const [loadingCount, setLoadingCount] = useState(0);
  const loading = loadingCount > 0;

  const [user, setUser] = useState({});
  const [reportData, setReportData] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [complinces, setComplinces] = useState([]);

  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState([]);

  const [activeFilter, setActiveFilter] = useState("");
  const [filterDateWise, setFilterDateWise] = useState({
    endDate: new Date(),
    startDate: new Date(),
  });

  const [selectedFilter, setSelectedFilter] = useState(null);
  const [complianceCategoryFilter, setComplianceCategoryFilter] = useState(null);
  const [criticalityRiskFilter, setCriticalityRiskFilter] = useState(null);
  const [statisticsRiskFilter, setStaticticsRiskFilter] = useState(null);
  const [companyOverviewFilter, setCompanyOverviewFilter] = useState(null);
  const [riskScoreFilter, setRiskScoreFilter] = useState(null);

  const [circleChart, setCircleChart] = useState([]);
  const [complianceBarData, setComplianceBarData] = useState([]);
  const [totalCompliance, setTotalCompliance] = useState(0);
  const [criticalityRiskData, setCriticalityRiskData] = useState([]);
  const [statisticsRiskData, setStatisticsRiskData] = useState([]);
  const [companyOverviewData, setCompanyOverviewData] = useState([]);

  const [activityLogs, setActivityLogs] = useState([]);
  const [filteredActivityLogs, setFilteredActivityLogs] = useState([]);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [userDetailsList, setUserDetailsList] = useState([]);
  const [auditorIdToName, setAuditorIdToName] = useState({});
  const [showGreetingConfetti, setShowGreetingConfetti] = useState(true);

  const formats = [
    { label: "Pdf", icon: "bi-file-earmark-pdf", value: "pdf" },
    { label: "Excel", icon: "bi-file-earmark-excel", value: "excel" },
  ];
  const periods = ["Monthly", "Quarterly", "Semi-Annually", "Annually"];

  const complianceStatuses = [
    { name: "Complied", key: "Complied" },
    { name: "Partial Complied", key: "Partial Complied" },
    { name: "Not Applicable", key: "Not Applicable" },
    { name: "Not Complied", key: "Not Complied" },
    { name: "Not Updated", key: "Not Updated" },
  ];

  const startLoading = () => setLoadingCount((prev) => prev + 1);
  const stopLoading = () => setLoadingCount((prev) => Math.max(0, prev - 1));

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        const data = await requestApi("/superadmin/userData", { method: "get" });
        setUser(data.data || {});
      } catch (error) {
        toast.error("Something went wrong while fetching user details.");
      } finally {
        stopLoading();
      }
    })();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowGreetingConfetti(false);
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        const { reports, statusCode } = await requestApi("/auditor/getReportOnDashboardComp", {
          method: "get",
        });

        if (statusCode === 200 && Array.isArray(reports)) {
          const allLegislations = reports.flatMap((item) => item.legislation || []);
          const uniqueLocations = [
            ...new Set(allLegislations.map((l) => l.location).filter(Boolean)),
          ];
          const uniqueCompanies = [
            ...new Set(allLegislations.map((l) => l.company_name).filter(Boolean)),
          ];
          setLocations(uniqueLocations);
          setCompanies(uniqueCompanies);
          setReportData(reports);
        } else {
          setReportData([]);
        }
      } catch (error) {
        toast.error("Something went wrong while fetching dashboard data.");
      } finally {
        stopLoading();
      }
    })();
  }, []);

  useEffect(() => {
    const pending = (complinces || []).filter((c) => c.open_closed != 1);

    const groupedByCompany = pending.reduce((acc, item) => {
      const key = item.company_name || "NA";
      if (!acc[key]) {
        acc[key] = { name: key, overdue: 0 };
      }
      if (item.due_date) {
        const d = new Date(item.due_date);
        if (!isNaN(d.getTime()) && d < new Date()) {
          acc[key].overdue += 1;
        }
      }
      return acc;
    }, {});

    const sortedCompanies = Object.values(groupedByCompany).sort((a, b) => b.overdue - a.overdue);
    const topRiskUpcomingDynamic = sortedCompanies.slice(0, 4).map((row, index) => {
      const baseIcon = index % 2 === 0 ? hdfcIcon : ltIcon;
      return {
        id: `${row.name}-${index}`,
        name: row.name,
        overdue: row.overdue,
        icon: baseIcon,
        iconAlt: `${row.name} icon`,
      };
    });

    if (!activeUpcomingId && topRiskUpcomingDynamic.length > 0) {
      setActiveUpcomingId(topRiskUpcomingDynamic[0].id);
    }
  }, [complinces, activeUpcomingId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await requestApi("/userRole", { method: "get" });
        const users = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

        const auditorMap = {};
        users.forEach((u) => {
          if (Number(u?.role_id) !== 2) return;
          const id = u?.id ?? u?._id ?? u?.user_id ?? u?.userId ?? u?.role_user_id;
          const name = u?.name ?? u?.full_name ?? u?.fullName ?? u?.username ?? u?.email;
          if (id != null && name) {
            auditorMap[String(id)] = String(name);
          }
        });
        setAuditorIdToName(auditorMap);

        const counts = users.reduce(
          (acc, u) => {
            const roleId = Number(u?.role_id);
            if (roleId === 2) acc.auditor += 1;
            else if (roleId === 3) acc.compliance += 1;
            else if (roleId === 4) acc.approver += 1;
            return acc;
          },
          { auditor: 0, compliance: 0, approver: 0 }
        );

        const totalUsers = counts.auditor + counts.compliance + counts.approver;
        setTotalUsersCount(totalUsers);
        setUserDetailsList([
          { id: "auditor", label: "Auditor", value: counts.auditor, barClass: "auditor" },
          {
            id: "compliance-officer",
            label: "Compliance Officer",
            value: counts.compliance,
            barClass: "compliance",
          },
          { id: "approver", label: "Approver", value: counts.approver, barClass: "approver" },
        ]);
      } catch (err) {
        setAuditorIdToName({});
        setTotalUsersCount(0);
        setUserDetailsList([
          { id: "auditor", label: "Auditor", value: 0, barClass: "auditor" },
          { id: "compliance-officer", label: "Compliance Officer", value: 0, barClass: "compliance" },
          { id: "approver", label: "Approver", value: 0, barClass: "approver" },
        ]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const id = await localStorage.getItem("role_id");
        if (!id) return;
        const res = await requestApi(`/activity/activitylog/${id}`, { method: "get" });
        setActivityLogs(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        // keep logs empty; avoid blocking dashboard rendering
      }
    })();
  }, []);

  useEffect(() => {
    if (!Array.isArray(reportData)) return;

    const filters = {
      startDate: filterDateWise.startDate,
      endDate: filterDateWise.endDate,
      companies: selectedCompany,
      locations: selectedLocation,
      // Apply date whenever a range is selected (Action page behavior)
      applyDateFilter: Boolean(
        activeFilter && filterDateWise.startDate && filterDateWise.endDate
      ),
    };

    setComplinces(flattenDashboardCompliances(reportData, filters));
    setFilteredReports(filterReportTreeByGlobalFilters(reportData, filters));
  }, [selectedCompany, selectedLocation, reportData, activeFilter, filterDateWise]);

  useEffect(() => {
    let filtered = Array.isArray(activityLogs) ? activityLogs : [];
    if (selectedCompany.length > 0) {
      filtered = filtered.filter((item) => selectedCompany.includes(item.company_name));
    }
    setFilteredActivityLogs(filtered);
  }, [activityLogs, selectedCompany]);

  useEffect(() => {
    if (!Array.isArray(complinces)) return;
    const filteredCompliance = filterCompliancesByDays(complinces, selectedFilter);

    const result = complianceStatuses.map((status) => ({
      name: status.name,
      value: filteredCompliance.filter((c) => c.current_compliance_status === status.key).length,
    }));
    setCircleChart(result);
  }, [selectedFilter, complinces]);

  useEffect(() => {
    if (!Array.isArray(complinces)) return;

    const filtered = filterCompliancesByDays(complinces, complianceCategoryFilter);

    const normalizeCategory = (str) =>
      str
        ?.trim()
        .toLowerCase()
        .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());

    const categories = [
      ...new Set(filtered.map((i) => normalizeCategory(i.compliance_categorization)).filter(Boolean)),
    ];

    const chartData = categories.map((category) => {
      const items = filtered.filter((item) => normalizeCategory(item.compliance_categorization) === category);
      return {
        name: category,
        complied: items.filter((i) => i.current_compliance_status === "Complied").length,
        notComplied: items.filter((i) => i.current_compliance_status === "Not Complied").length,
        notApplicable: items.filter((i) => i.current_compliance_status === "Not Applicable").length,
        notUpdated: items.filter((i) => i.current_compliance_status === "Not Updated").length,
        compiledWithException: items.filter((i) => i.current_compliance_status === "Partial Complied").length,
      };
    });

    setComplianceBarData(chartData);
    setTotalCompliance(sumBarChartComplianceStatuses(filtered));
  }, [complianceCategoryFilter, complinces]);

  useEffect(() => {
    const filteredCompliance = filterCompliancesByDays(complinces, criticalityRiskFilter);

    const getCount = (status, criticality) =>
      filteredCompliance.filter(
        (c) => c.current_compliance_status === status && c.criticality === criticality
      ).length;

    const criticalities = [
      ...new Set((complinces || []).map((item) => item.criticality?.trim()).filter(Boolean)),
    ];

    const computedData = criticalities.map((level) => {
      const statusCounts = complianceStatuses.reduce((acc, status) => {
        acc[status.key] = getCount(status.key, level) || "-";
        return acc;
      }, {});

      return {
        category: level,
        ...statusCounts,
        total: Object.values(statusCounts).reduce((sum, val) => (val !== "-" ? sum + val : sum), 0),
      };
    });

    const totalRow = {
      category: "Total",
      ...complianceStatuses.reduce((acc, status) => {
        acc[status.key] = computedData.reduce(
          (sum, row) => (row[status.key] !== "-" ? sum + row[status.key] : sum),
          0
        );
        return acc;
      }, {}),
      total: computedData.reduce((sum, row) => sum + row.total, 0),
    };

    const criticalityOrder = ["High", "Medium", "Low"];
    const sortedData = computedData.sort(
      (a, b) => criticalityOrder.indexOf(a.category) - criticalityOrder.indexOf(b.category)
    );

    setCriticalityRiskData([...sortedData, totalRow]);
  }, [criticalityRiskFilter, complinces]);

  const filteredComplianceForStats = useMemo(
    () => filterCompliancesByDays(complinces, statisticsRiskFilter),
    [statisticsRiskFilter, complinces]
  );

  useEffect(() => {
    const categories = ["High", "Medium", "Low"];
    const statusLabels = {
      Complied: "Complied",
      "Partial Complied": "Partial",
      "Not Applicable": "NotApplicable",
      "Not Complied": "NonComplied",
      "Not Updated": "NonUpdated",
    };

    const stats = categories.map((cat) => {
      const items = (filteredComplianceForStats || []).filter((i) => i.criticality === cat);
      const statusCounts = Object.values(statusLabels).reduce((acc, label) => {
        acc[label] = 0;
        return acc;
      }, {});

      items.forEach((item) => {
        const status = item.current_compliance_status;
        const key = statusLabels[status];
        if (key) statusCounts[key]++;
      });

      return { category: cat, ...statusCounts };
    });

    const finalStats = stats.map((row) => ({
      category: row.category,
      Complied: row.Complied || 0,
      Partial: row.Partial || 0,
      NotApplicable: row.NotApplicable || 0,
      NonComplied: row.NonComplied || 0,
      NonUpdated: row.NonUpdated || 0,
    }));

    setStatisticsRiskData(finalStats);
  }, [filteredComplianceForStats]);

  const filteredCompanyCompliance = useMemo(
    () => filterCompliancesByDays(complinces, companyOverviewFilter),
    [companyOverviewFilter, complinces]
  );

  useEffect(() => {
    const groupedData = {};

    (filteredCompanyCompliance || []).forEach((item) => {
      const companyKey = item.company_name;
      if (!groupedData[companyKey]) {
        const auditorId = item.assigned_auditor_id ?? item.assignedAuditorId ?? item.assigned_auditor ?? null;
        groupedData[companyKey] = {
          company_name: companyKey,
          branch_ids: new Set(),
          compliance_categories: new Set(),
          total_dues: 0,
          total_compliances: 0,
          done_compliances: 0,
          auditor:
            (auditorId != null && auditorIdToName[String(auditorId)]) ||
            item.assignedAuditor ||
            item.assigned_auditor_name ||
            item.auditor ||
            item.auditor_name ||
            "NA",
          approver: item.approver || "NA",
        };
      }

      if (groupedData[companyKey].auditor === "NA") {
        const auditorId = item.assigned_auditor_id ?? item.assignedAuditorId ?? item.assigned_auditor ?? null;
        groupedData[companyKey].auditor =
          (auditorId != null && auditorIdToName[String(auditorId)]) ||
          item.assignedAuditor ||
          item.assigned_auditor_name ||
          item.auditor ||
          item.auditor_name ||
          "NA";
      }
      groupedData[companyKey].branch_ids.add(item.branch_id);
      groupedData[companyKey].compliance_categories.add(item.compliance_categorization);
      if (item.open_closed == 1) groupedData[companyKey].done_compliances++;
      if (item.due_date) groupedData[companyKey].total_compliances += 1;
    });

    Object.values(groupedData).forEach((companyRow) => {
      companyRow.total_dues = companyRow.total_compliances - companyRow.done_compliances;
    });

    const overviewData = Object.values(groupedData).map((companyRow) => ({
      company_name: companyRow.company_name,
      branch_count: companyRow.branch_ids.size,
      category_count: companyRow.compliance_categories.size,
      total_dues: companyRow.total_dues,
      total_compliances: companyRow.total_compliances,
      auditor: companyRow.auditor,
      approver: companyRow.approver,
    }));

    setCompanyOverviewData(overviewData);
  }, [filteredCompanyCompliance, auditorIdToName]);

  const exportReport = (data, format, period) => {
    const now = new Date();

    const subtractMonths = (date, numMonths) => {
      const d = new Date(date);
      d.setMonth(d.getMonth() - numMonths);
      if (d.getDate() !== date.getDate()) {
        d.setDate(0);
      }
      return d;
    };

    let monthsBack;
    switch (period) {
      case "Monthly":
        monthsBack = 1;
        break;
      case "Quarterly":
        monthsBack = 3;
        break;
      case "Semi-Annually":
        monthsBack = 6;
        break;
      case "Annually":
        monthsBack = 12;
        break;
      default:
        monthsBack = 0;
    }

    const startDate = subtractMonths(now, monthsBack);

    let srNo = 1;
    const flatExportData = [];

    (data || []).forEach((entry) => {
      const { id: report_id, created_by, updated_by, legislation = [] } = entry || {};

      (legislation || []).forEach((leg) => {
        const {
          legislation_name,
          status,
          created_at: legislation_created_at,
          compliance = [],
          location,
          company_name,
          branch_id,
        } = leg || {};

        const legCreatedAt = new Date(legislation_created_at);
        if (isNaN(legCreatedAt) || legCreatedAt < startDate || legCreatedAt > now) {
          return;
        }

        if (!Array.isArray(compliance) || compliance.length === 0) {
          flatExportData.push({
            sr_no: srNo++,
            report_id,
            company_name,
            branch_id,
            location,
            created_by,
            updated_by,
            legislation_name,
            legislation_status: status,
            legislation_created_at,
            compliance: "N/A",
            provision: "N/A",
            compliance_description: "N/A",
            compliance_type: "N/A",
            penalty_description: "N/A",
            approver: "N/A",
            due_date: "N/A",
            criticality: "N/A",
            current_compliance_status: "N/A",
          });
        } else {
          compliance.forEach((comp) => {
            flatExportData.push({
              sr_no: srNo++,
              report_id,
              company_name,
              branch_id,
              location,
              created_by,
              updated_by,
              legislation_name,
              legislation_status: status,
              legislation_created_at,
              compliance: comp?.compliance || "N/A",
              provision: comp?.provision || "N/A",
              compliance_description: comp?.compliance_description || "N/A",
              compliance_type: comp?.compliance_type || "N/A",
              penalty_description: comp?.penalty_description || "N/A",
              approver: comp?.approver || "N/A",
              due_date: comp?.due_date ? new Date(comp.due_date).toLocaleDateString() : "N/A",
              criticality: comp?.criticality || "N/A",
              current_compliance_status: comp?.current_compliance_status || "N/A",
            });
          });
        }
      });
    });

    if (flatExportData.length === 0) {
      toast.error("No data found for the selected period.");
      return;
    }

    const dashboardHeaderMap = {
      sr_no: "Sr No",
      report_id: "Report ID",
      company_name: "Entity",
      branch_id: "Branch Name",
      location: "Location",
      created_by: "Created By",
      updated_by: "Updated By",
      legislation_name: "Act",
      legislation_status: "Legislation Status",
      legislation_created_at: "Legislation Created At",
      compliance: "Compliance Description",
      provision: "Provision",
      compliance_description: "Compliance Description",
      compliance_type: "Compliance Type",
      penalty_description: "Penalty Description",
      approver: "Approver",
      due_date: "Due Date",
      criticality: "Criticality Level",
      current_compliance_status: "Current Status",
    };

    const formattedExportData = flatExportData.map((item) => {
      const formatted = {};
      for (const key in item) {
        const label =
          dashboardHeaderMap[key] ||
          key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        formatted[label] = item[key];
      }
      return formatted;
    });

    if (format === "excel") {
      let lastReportId = null;
      flatExportData.forEach((row, idx) => {
        if (row.report_id === lastReportId) {
          formattedExportData[idx]["Report ID"] = "";
          formattedExportData[idx]["Entity"] = "";
          formattedExportData[idx]["Branch Name"] = "";
          formattedExportData[idx]["Location"] = "";
          formattedExportData[idx]["Created By"] = "";
          formattedExportData[idx]["Updated By"] = "";
        } else {
          lastReportId = row.report_id;
        }
      });

      const displayHeaders = Object.keys(formattedExportData[0] || {});

      const uniqueCompanies = [
        ...new Set(
          flatExportData
            .map((item) => item.company_name || item.companyName)
            .filter(Boolean)
        ),
      ];
      let companyNameText = "";
      if (uniqueCompanies.length > 0) {
        companyNameText = uniqueCompanies.join(", ");
      } else if (
        typeof selectedCompany !== "undefined" &&
        selectedCompany &&
        selectedCompany.length > 0
      ) {
        companyNameText = Array.isArray(selectedCompany)
          ? selectedCompany.join(", ")
          : selectedCompany;
      } else {
        companyNameText = "Company Name";
      }

      const uniqueLocations = [
        ...new Set(
          flatExportData
            .map((item) => item.location)
            .filter(Boolean)
        ),
      ];
      let locationText = "";
      if (uniqueLocations.length > 0) {
        locationText = uniqueLocations.join(", ");
      } else if (
        typeof selectedLocation !== "undefined" &&
        selectedLocation &&
        selectedLocation.length > 0
      ) {
        locationText = Array.isArray(selectedLocation)
          ? selectedLocation.join(", ")
          : selectedLocation;
      } else {
        locationText = "All Locations";
      }

      let dateRangeText = "";
      if (
        typeof filterDateWise !== "undefined" &&
        filterDateWise &&
        filterDateWise.startDate &&
        filterDateWise.endDate
      ) {
        dateRangeText = `${filterDateWise.startDate} to ${filterDateWise.endDate}`;
      } else if (period) {
        dateRangeText = period;
      } else {
        dateRangeText = "All";
      }

      const getColumnLetter = (colIndex) => {
        let temp,
          letter = "";
        while (colIndex > 0) {
          temp = (colIndex - 1) % 26;
          letter = String.fromCharCode(65 + temp) + letter;
          colIndex = (colIndex - temp - 1) / 26;
        }
        return letter;
      };

      const totalCols = displayHeaders.length;
      const lastColLetter = getColumnLetter(totalCols);
      const totalRows = 4 + formattedExportData.length;

      const worksheet = XLSX.utils.json_to_sheet(formattedExportData, {
        origin: "A4",
      });

      const colWidths = displayHeaders.map((header) => ({
        wch: Math.max(header.length + 5, 18),
      }));
      worksheet["!cols"] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

      XlsxPopulate.fromDataAsync(wbout)
        .then((wb) => {
          const sheet = wb.sheet(0);

          // 1. Row 1: Company Name (Left Aligned)
          const companyRange = sheet.range(`A1:${lastColLetter}1`);
          companyRange.merged(true);
          sheet.cell("A1").value(`Company Name: ${companyNameText}`);
          companyRange.style({
            bold: true,
            fontSize: 14,
            wrapText: true,
            horizontalAlignment: "left",
            verticalAlignment: "center",
            fill: "F2F2F2",
            border: true,
          });

          // 2. Row 2: Location (Left Aligned)
          const locationRange = sheet.range(`A2:${lastColLetter}2`);
          locationRange.merged(true);
          sheet.cell("A2").value(`Location: ${locationText}`);
          locationRange.style({
            bold: true,
            fontSize: 11,
            wrapText: true,
            horizontalAlignment: "left",
            verticalAlignment: "center",
            fill: "F2F2F2",
            border: true,
          });

          // 3. Row 3: Period (Left Aligned)
          const dateRange = sheet.range(`A3:${lastColLetter}3`);
          dateRange.merged(true);
          sheet.cell("A3").value(`Period: ${dateRangeText}`);
          dateRange.style({
            bold: true,
            fontSize: 11,
            wrapText: true,
            horizontalAlignment: "left",
            verticalAlignment: "center",
            fill: "F2F2F2",
            border: true,
          });

          // 4. Headings (Row 4): bold, larger text size, text wrapping, highlighted background, borders
          sheet.range(`A4:${lastColLetter}4`).style({
            bold: true,
            fontSize: 12,
            wrapText: true,
            horizontalAlignment: "center",
            verticalAlignment: "center",
            fill: "D9E1F2",
            border: true,
          });

          // 5. Data Contents (Row 5..totalRows): text wrapping, vertical alignment, borders
          if (totalRows >= 5) {
            sheet.range(`A5:${lastColLetter}${totalRows}`).style({
              wrapText: true,
              fontSize: 10,
              verticalAlignment: "center",
              border: true,
            });
          }

          return wb.outputAsync();
        })
        .then((blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const a = document.createElement("a");
          a.href = url;
          a.download = "Report.xlsx";
          a.click();
          window.URL.revokeObjectURL(url);
        });
    } else if (format === "pdf") {
      const newWindow = window.open();
      if (!newWindow) return;

      const tableHeaders = Object.keys(formattedExportData[0] || {});
      const mergedRows = [];
      const rowSpans = {};
      const rendered = new Set();

      flatExportData.forEach((item) => {
        const key = item.report_id;
        if (!rowSpans[key]) {
          rowSpans[key] = flatExportData.filter((i) => i.report_id === key).length;
        }
      });

      flatExportData.forEach((item, idx) => {
        const formattedItem = formattedExportData[idx];
        let row = "<tr>";
        tableHeaders.forEach((headerLabel) => {
          if (["Report ID", "Entity", "Branch Name", "Location", "Created By", "Updated By"].includes(headerLabel)) {
            if (!rendered.has(item.report_id)) {
              row += `<td rowspan="${rowSpans[item.report_id]}">${formattedItem[headerLabel]}</td>`;
            }
          } else {
            row += `<td>${formattedItem[headerLabel]}</td>`;
          }
        });
        row += "</tr>";
        mergedRows.push(row);
        rendered.add(item.report_id);
      });

      const htmlContent = `
        <html>
          <head>
            <title>Report</title>
            <style>
              table { width: 100%; border-collapse: collapse; font-size: 10px; }
              th, td { border: 1px solid #000; padding: 4px; text-align: left; vertical-align: top; }
            </style>
          </head>
          <body>
            <h2>Filtered Report - ${period}</h2>
            <table>
              <thead>
                <tr>${tableHeaders.map((h) => `<th>${h}</th>`).join("")}</tr>
              </thead>
              <tbody>${mergedRows.join("")}</tbody>
            </table>
          </body>
        </html>
      `;

      newWindow.document.write(htmlContent);
      newWindow.document.close();
      newWindow.print();
    }
  };

  const applyDateFilter = (range = filterDateWise) => {
    if (!range.startDate || !range.endDate) return;
    setFilterDateWise(range);
    if (!activeFilter || activeFilter === "Manual") setActiveFilter("Manual");
  };

  const dashboardStats = useMemo(() => {
    const companiesSet = new Set();
    const branchesSet = new Set();

    (complinces || []).forEach((comp) => {
      if (comp.company_name) companiesSet.add(comp.company_name);
      if (comp.branch_id) branchesSet.add(comp.branch_id);
    });

    const pending = (complinces || []).filter((c) => c.open_closed != 1);
    const overdueCount = pending.filter((c) => {
      if (!c.due_date) return false;
      const d = new Date(c.due_date);
      if (isNaN(d.getTime())) return false;
      return d < new Date();
    }).length;

    const dueIn7DaysCount = pending.filter((c) => {
      if (!c.due_date) return false;
      const d = new Date(c.due_date);
      if (isNaN(d.getTime())) return false;
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      end.setHours(23, 59, 59, 999);
      return d >= start && d <= end;
    }).length;

    return [
      {
        id: "companies",
        value: String(companiesSet.size),
        label: "Total Companies",
        icon: company,
        iconAlt: "Companies",
        theme: "purple",
      },
      {
        id: "branches",
        value: String(branchesSet.size),
        label: "Total Branches",
        icon: branches,
        iconAlt: "Branches",
        theme: "blue",
      },
      {
        id: "compliances",
        value: String((complinces || []).length),
        label: "Total compliances",
        icon: compliances,
        iconAlt: "Compliances",
        theme: "green",
      },
      {
        id: "overdue",
        value: String(overdueCount),
        label: "Overdue",
        icon: overdue,
        iconAlt: "Overdue",
        theme: "orange",
      },
      {
        id: "due",
        value: String(dueIn7DaysCount),
        label: "Due in 7 Days",
        icon: due,
        iconAlt: "Due in 7 days",
        theme: "red",
      },
    ];
  }, [filteredReports, complinces]);

  const complianceBreakdown = useMemo(() => {
    const byName = new Map((circleChart || []).map((x) => [x.name, x.value]));
    return [
      { id: "complied", label: "Complied", value: byName.get("Complied") || 0, colorClass: "teal" },
      {
        id: "partial",
        label: "Partial Complied",
        value: byName.get("Partial Complied") || 0,
        colorClass: "yellow",
      },
      { id: "na", label: "Not Applicable", value: byName.get("Not Applicable") || 0, colorClass: "gray" },
      { id: "non", label: "Non Complied", value: byName.get("Not Complied") || 0, colorClass: "red" },
      { id: "pending", label: "Not Updated", value: byName.get("Not Updated") || 0, colorClass: "blue" },
    ];
  }, [circleChart]);

  const handleComplianceOverviewClick = (item) => {
    if (!item) return;
    const statusMap = {
      complied: "Complied",
      partial: "Partial Complied",
      na: "Not Applicable",
      non: "Not Complied",
      pending: "Not Updated",
    };
    const targetStatus =
      statusMap[item.id] || item.statusKey || (item.label === "Non Complied" ? "Not Complied" : item.label);

    let startDate = null;
    let endDate = null;
    let activeDateLabel = "";
    let shouldApplyDateFilter = false;

    if (selectedFilter) {
      const days = parseInt(selectedFilter, 10);
      if (!isNaN(days) && days > 0) {
        const now = new Date();
        endDate = new Date(now);
        startDate = new Date(now);
        startDate.setDate(now.getDate() - days);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        activeDateLabel = `Last ${days} Days`;
        shouldApplyDateFilter = true;
      }
    } else if (activeFilter && filterDateWise?.startDate && filterDateWise?.endDate) {
      startDate = filterDateWise.startDate;
      endDate = filterDateWise.endDate;
      activeDateLabel = activeFilter;
      shouldApplyDateFilter = true;
    }

    navigate("/compliancer/compliencer-action", {
      state: {
        status: targetStatus,
        selectedStatus: [targetStatus],
        selectedCompany: selectedCompany || [],
        selectedCompanies: selectedCompany || [],
        selectedLocation: selectedLocation || [],
        filterDateWise: shouldApplyDateFilter ? { startDate, endDate } : null,
        startDate: shouldApplyDateFilter ? startDate : null,
        endDate: shouldApplyDateFilter ? endDate : null,
        activeFilter: activeDateLabel,
        applyDateFilter: shouldApplyDateFilter,
      },
    });
  };

  const pieChartSegments = useMemo(() => {
    const total = complianceBreakdown.reduce((sum, item) => sum + (Number(item.value) || 0), 0) || 1;
    const toPercent = (n) => Math.max((Number(n) / total) * 100, 0);
    return [
      { id: "complied", value: toPercent(complianceBreakdown[0]?.value), color: "#36b89c" },
      { id: "partial", value: toPercent(complianceBreakdown[1]?.value), color: "#f5d167" },
      { id: "na", value: toPercent(complianceBreakdown[2]?.value), color: "#b8bcc6" },
      { id: "non", value: toPercent(complianceBreakdown[3]?.value), color: "#ff3447" },
      { id: "pending", value: toPercent(complianceBreakdown[4]?.value), color: "#00c0e8" },
    ];
  }, [complianceBreakdown]);

  // (legacy SVG pie constants removed; Recharts pie handles rendering)

  const complianceCategoriesSeries = useMemo(() => {
    return (complianceBarData || []).slice(0, 10).map((item, idx) => ({
      id: `cat-${idx + 1}`,
      label: item.name,
      complied: item.complied || 0,
      partial: item.compiledWithException || 0,
      notApplicable: item.notApplicable || 0,
      nonComplied: item.notComplied || 0,
    }));
  }, [complianceBarData]);

  const complianceCategoriesMax = useMemo(() => {
    const max = Math.max(
      1,
      ...complianceCategoriesSeries.map(
        (x) => (x.complied || 0) + (x.partial || 0) + (x.notApplicable || 0) + (x.nonComplied || 0)
      )
    );
    const step = Math.ceil(max / 6);
    return step * 6;
  }, [complianceCategoriesSeries]);
  const complianceCategoriesMin = 0;
  const complianceCategoriesYAxis = useMemo(() => {
    const step = complianceCategoriesMax / 6;
    return [6, 5, 4, 3, 2, 1, 0].map((m) => Math.round(m * step));
  }, [complianceCategoriesMax]);

  const riskStatisticsMax = useMemo(() => {
    const max = Math.max(
      1,
      ...statisticsRiskData.map(
        (x) => (x.Complied || 0) + (x.Partial || 0) + (x.NotApplicable || 0) + (x.NonComplied || 0)
      )
    );
    const step = Math.ceil(max / 6);
    return step * 6;
  }, [statisticsRiskData]);
  const riskStatisticsYAxis = useMemo(() => {
    const step = riskStatisticsMax / 6;
    return [6, 5, 4, 3, 2, 1, 0].map((m) => Math.round(m * step));
  }, [riskStatisticsMax]);
  const totalRiskStatistics = useMemo(
    () => sumBarChartComplianceStatuses(filteredComplianceForStats),
    [filteredComplianceForStats]
  );

  const criticalityRiskRows = useMemo(() => {
    return (criticalityRiskData || []).map((row, idx) => ({
      id: `crit-${idx}`,
      criticality: row.category,
      complied: row["Complied"],
      partialComplied: row["Partial Complied"],
      notApplicable: row["Not Applicable"],
      notComplied: row["Not Complied"],
      total: row.total,
      isTotal: row.category === "Total",
    }));
  }, [criticalityRiskData]);

  const riskStatisticsSeries = useMemo(() => {
    return (statisticsRiskData || []).map((row) => ({
      id: String(row.category).toLowerCase(),
      label: row.category,
      complied: row.Complied || 0,
      partial: row.Partial || 0,
      notApplicable: row.NotApplicable || 0,
      nonComplied: row.NonComplied || 0,
    }));
  }, [statisticsRiskData]);

  const activityLogRows = useMemo(() => {
    return (filteredActivityLogs || []).slice(0, 6).map((item, idx) => ({
      id: item._id || item.id || `log-${idx}`,
      srNo: `${idx + 1}.`,
      companyName: item.company_name || "NA",
      userName: item.compliancer || "NA",
      userType: item.role || "Compliance Officer",
      dateTime: item.created_at
        ? new Date(item.created_at)
            .toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })
            .replace(/\//g, "-")
            .replace(", ", " at ")
        : "NA",
      activity: item.latest_activity || "NA",
    }));
  }, [filteredActivityLogs]);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <main className="new-dashboard-page">
      <section className="lyt-section">
        <Container fluid className="typ-container">
        <Row className="nd-bottom-row">
            <Col xs={12}>
              <div className="nd-panel">
                <div className="nd-header">
                  <p className="nd-greeting">
                    {showGreetingConfetti && (
                      <span className="nd-greeting-confetti" aria-hidden="true">
                        <img src={celebrateGif} alt="" className="nd-greeting-confetti-image" />
                      </span>
                    )}
                    <span className="nd-greeting-name">Hello {user?.name || "User"}!</span>
                    <span className="nd-greeting-text">
                      {" "}
                      - here&apos;s what&apos;s happening with your panel.
                    </span>
                  </p>
                  <div className="nd-actions">
                    <Dropdown
                      label="Location"
                      options={locations}
                      selectedItems={selectedLocation}
                      setSelectedItems={setSelectedLocation}
                    />
                    <Dropdown
                      label="Company Name"
                      options={companies}
                      selectedItems={selectedCompany}
                      setSelectedItems={setSelectedCompany}
                    />

                    <CalenderDropdown
                      activeFilter={activeFilter}
                      setActiveFilter={setActiveFilter}
                      filterDateWise={filterDateWise}
                      setFilterDateWise={setFilterDateWise}
                      applyDateFilter={applyDateFilter}
                    />

                    <ExportDropdown
                      formats={formats}
                      periods={periods}
                      exportReport={exportReport}
                      filteredReports={filteredReports}
                    />
                  </div>
                </div>

                <div className="nd-stats">
                  {dashboardStats.map((item) => (
                    <article
                      className="nd-stat-card"
                      key={item.id}
                    >
                      <div className={`nd-stat-icon nd-stat-icon-${item.theme}`}>
                        {item.icon ? (
                          <img src={item.icon} alt={item.iconAlt} />
                        ) : (
                          <i className={item.iconClass} />
                        )}
                      </div>
                      <div className="nd-stat-content">
                        <p className={`nd-stat-value nd-stat-value-${item.theme}`}>
                          {item.value}
                        </p>
                        <p className="nd-stat-label">{item.label}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>


      <section className="lyt-section ">
        <Container fluid className="typ-container">
          <Row>
            <Col lg={4} md={12}>
              <article className="nd-overview-card">
                <div className="nd-overview-header">
                  <p className="nd-overview-title">Compliance Overview</p>
                  <select
                    className="nd-overview-filter"
                    value={selectedFilter || ""}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                  >
                    <option value="" hidden>
                      Monthly
                    </option>
                    <option value={7}>Last seven days</option>
                    <option value={14}>Last two weeks</option>
                    <option value={21}>Last three weeks</option>
                    <option value={30}>Last one month</option>
                  </select>
                </div>

                <div className="nd-overview-content">
                  <div className="nd-pie-chart" aria-label="Compliance overview">
                    <div className="nd-pie-rechart">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                          <Pie
                            data={pieChartSegments}
                            dataKey="value"
                            cx="50%"
                            cy="50%"
                            innerRadius="52%"
                            outerRadius="90%"
                            minAngle={6}
                            paddingAngle={1}
                            cornerRadius={6}
                            activeIndex={activePieIndex}
                            activeShape={renderActivePieSlice}
                            onMouseEnter={(_, index) => setActivePieIndex(index)}
                            onMouseLeave={() => setActivePieIndex(null)}
                            isAnimationActive
                            animationDuration={320}
                            animationEasing="ease-out"
                            stroke="#ffffff"
                            strokeWidth={2}
                          >
                            {pieChartSegments.map((segment) => (
                              <Cell key={segment.id} fill={segment.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="nd-pie-inner">
                      <p className="nd-pie-total">
                        {filterCompliancesByDays(complinces || [], selectedFilter)?.length || 0}
                      </p>
                      <p className="nd-pie-label">Total Compliances</p>
                    </div>
                  </div>

                  <div className="nd-overview-legend">
                    {complianceBreakdown.map((item) => (
                      <div
                        className="nd-legend-row"
                        key={item.id}
                        onClick={() => handleComplianceOverviewClick(item)}
                        style={{ cursor: "pointer" }}
                        title={`Click to view ${item.label} compliances`}
                      >
                        <span className="nd-legend-left">
                          <span className={`nd-legend-dot nd-legend-dot-${item.colorClass}`} />
                          {item.label}
                        </span>
                        <span className="nd-legend-value">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            </Col>

            <Col lg={4} md={6}>
              <article className="nd-overview-card">
                <div className="nd-overview-header">
                  <p className="nd-overview-title">Top Risk & Upcoming</p>
                  <button
                    type="button"
                    className="nd-link-btn"
                    onClick={() => navigate("/compliancer/compliencer-action")}
                  >
                    View All
                    <span className="nd-link-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="14" height="14" focusable="false" aria-hidden="true">
                        <path
                          d="M9 6l6 6-6 6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                </div>

                <div className="nd-upcoming-list">
                  {(() => {
                    const pending = (complinces || []).filter((c) => c.open_closed != 1);
                    const groupedByCompany = pending.reduce((acc, item) => {
                      const key = item.company_name || "NA";
                      if (!acc[key]) {
                        acc[key] = { name: key, overdue: 0 };
                      }
                      if (item.due_date) {
                        const d = new Date(item.due_date);
                        if (!isNaN(d.getTime()) && d < new Date()) {
                          acc[key].overdue += 1;
                        }
                      }
                      return acc;
                    }, {});
                    const dynamicList = Object.values(groupedByCompany)
                      .sort((a, b) => b.overdue - a.overdue)
                      .slice(0, 4)
                      .map((row, index) => ({
                        id: `${row.name}-${index}`,
                        name: row.name,
                        overdue: row.overdue,
                        icon: index % 2 === 0 ? hdfcIcon : ltIcon,
                        iconAlt: `${row.name} icon`,
                      }));
                    return dynamicList;
                  })().map((item) => (
                    <div
                      className={`nd-upcoming-row ${
                        activeUpcomingId === item.id ? "nd-upcoming-row-active" : ""
                      }`}
                      key={item.id}
                      onMouseEnter={() => setActiveUpcomingId(item.id)}
                      onMouseLeave={() => setActiveUpcomingId(null)}
                    >
                      <span className="nd-upcoming-left">
                        {item.icon ? (
                          <img src={item.icon} alt={item.iconAlt} className="nd-upcoming-company-icon" />
                        ) : (
                          <i className={`bi bi-bank2 nd-upcoming-icon-${item.iconTheme}`} />
                        )}
                        {item.name}
                      </span>
                      <span className="nd-upcoming-right">
                        <span className="nd-upcoming-count">{item.overdue}</span>
                        <span className="nd-upcoming-label">Overdue</span>
                      </span>
                    </div>
                  ))}
                </div>
              </article>
            </Col>

            <Col lg={4} md={6}>
              <article className="nd-overview-card">
                <div className="nd-overview-header">
                  <p className="nd-overview-title">Risk Score</p>
                  <select
                    className="nd-overview-filter"
                    value={riskScoreFilter || ""}
                    onChange={(e) => setRiskScoreFilter(e.target.value)}
                  >
                    <option value="" hidden>
                      Monthly
                    </option>
                    <option value={7}>Last seven days</option>
                    <option value={14}>Last two weeks</option>
                    <option value={21}>Last three weeks</option>
                    <option value={30}>Last one month</option>
                  </select>
                </div>

                <div className="nd-risk-score-list">
                  {(() => {
                    const pendingAll = (complinces || []).filter((c) => c.open_closed != 1);
                    const pending = filterCompliancesByDays(pendingAll, riskScoreFilter);
                    const counts = pending.reduce(
                      (acc, item) => {
                        const key = (item.criticality || "").toLowerCase();
                        if (key === "high") acc.high += 1;
                        if (key === "medium") acc.medium += 1;
                        if (key === "low") acc.low += 1;
                        return acc;
                      },
                      { high: 0, medium: 0, low: 0 }
                    );
                    const dynamicList = [
                      {
                        id: "high",
                        score: counts.high,
                        label: "High Risk",
                        className: "high",
                        icon: highRisk,
                        iconAlt: "High risk",
                      },
                      {
                        id: "medium",
                        score: counts.medium,
                        label: "Medium Risk",
                        className: "medium",
                        icon: mediumRisk,
                        iconAlt: "Medium risk",
                      },
                      {
                        id: "low",
                        score: counts.low,
                        label: "Low Risk",
                        className: "low",
                        icon: lowRisk,
                        iconAlt: "Low risk",
                      },
                    ];
                    return dynamicList;
                  })().map((item) => (
                    <div className={`nd-risk-score-item nd-risk-score-item-${item.className}`} key={item.id}>
                      <span className="nd-risk-score-left">
                        <img src={item.icon} alt={item.iconAlt} className="nd-risk-score-icon" />
                        <span className={`nd-risk-score-value nd-risk-score-value-${item.className}`}>
                          {item.score}
                        </span>
                      </span>
                      <span className="nd-risk-score-right">{item.label}</span>
                    </div>
                  ))}
                </div>
              </article>
            </Col>
          </Row>
        </Container>
      </section>



      <section className="lyt-section">
        <Container fluid className="typ-container">
          <Row className="nd-compliance-categories-row">
            <Col xs={12}>
              <article className="nd-overview-card nd-compliance-categories-card">
                <div className="nd-overview-header nd-compliance-categories-header">
                  <p className="nd-overview-title">COMPLIANCE CATEGORIES</p>
                  <div className="nd-compliance-categories-actions">
                    <div className="nd-compliance-categories-legend">
                      {complianceCategoriesLegend.map((item) => (
                        <span className="nd-risk-legend-item" key={item.id}>
                          <span className={`nd-risk-legend-dot nd-risk-legend-dot-${item.colorClass}`} />
                          {item.label}
                        </span>
                      ))}
                    </div>
                    <div className="nd-compliance-categories-tools">
                      <p className="nd-risk-total-label">
                        Total: <span>{totalCompliance}</span>
                      </p>
                      <select
                        className="nd-overview-filter"
                        value={complianceCategoryFilter || ""}
                        onChange={(e) => setComplianceCategoryFilter(e.target.value)}
                      >
                        <option value="" hidden>
                          Monthly
                        </option>
                        <option value={7}>Last seven days</option>
                        <option value={14}>Last two weeks</option>
                        <option value={21}>Last three weeks</option>
                        <option value={30}>Last one month</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="nd-compliance-categories-grid">
                  <div className="nd-compliance-categories-y-axis">
                    {complianceCategoriesYAxis.map((value) => (
                      <span className="nd-risk-chart-y-tick" key={value}>
                        {value}
                      </span>
                    ))}
                  </div>

                  <div className="nd-compliance-categories-body">
                    {complianceCategoriesYAxis.map((value) => {
                      const isTopTick = value === complianceCategoriesMax;

                      return (
                        <span
                          className="nd-compliance-categories-line"
                          key={value}
                          style={{
                            bottom: `calc(var(--nd-risk-label-space) + ((${value} - ${complianceCategoriesMin}) / (${complianceCategoriesMax} - ${complianceCategoriesMin})) * var(--nd-risk-plot-height)${isTopTick ? " - 1px" : ""})`,
                          }}
                        />
                      );
                    })}

                    <div className="nd-compliance-categories-groups">
                      {complianceCategoriesSeries.map((item) => {
                        const visibleRange = complianceCategoriesMax - complianceCategoriesMin || 1;
                        const totalValue = item.complied + item.partial + item.notApplicable + item.nonComplied;
                        const visibleComplied = item.complied;
                        const visiblePartial = item.partial;
                        const visibleNotApplicable = item.notApplicable;
                        const visibleNonComplied = item.nonComplied;

                        return (
                        <div className="nd-compliance-categories-group" key={item.id}>
                          <div className="nd-compliance-categories-bar">
                            <span
                              className="nd-compliance-categories-segment nd-compliance-categories-segment-complied"
                              style={{ height: `${(visibleComplied / visibleRange) * 100}%` }}
                            />
                            <span
                              className="nd-compliance-categories-segment nd-compliance-categories-segment-partial"
                              style={{ height: `${(visiblePartial / visibleRange) * 100}%` }}
                            />
                            <span
                              className="nd-compliance-categories-segment nd-compliance-categories-segment-not-applicable"
                              style={{ height: `${(visibleNotApplicable / visibleRange) * 100}%` }}
                            />
                            <span
                              className="nd-compliance-categories-segment nd-compliance-categories-segment-non-complied"
                              style={{ height: `${(visibleNonComplied / visibleRange) * 100}%` }}
                            />
                          </div>
                          <div className="nd-compliance-categories-tooltip">
                            <div className="nd-compliance-categories-tooltip-title">
                              <span>{item.label}</span>
                              <strong>{totalValue}</strong>
                            </div>
                            <div className="nd-compliance-categories-tooltip-row">
                              <span className="nd-compliance-categories-tooltip-left">
                                <span className="nd-risk-legend-dot nd-risk-legend-dot-complied" />
                                Complied
                              </span>
                              <strong>{item.complied}</strong>
                            </div>
                            <div className="nd-compliance-categories-tooltip-row">
                              <span className="nd-compliance-categories-tooltip-left">
                                <span className="nd-risk-legend-dot nd-risk-legend-dot-partial" />
                                Partial Complied
                              </span>
                              <strong>{item.partial}</strong>
                            </div>
                            <div className="nd-compliance-categories-tooltip-row">
                              <span className="nd-compliance-categories-tooltip-left">
                                <span className="nd-risk-legend-dot nd-risk-legend-dot-not-applicable" />
                                Not Applicable
                              </span>
                              <strong>{item.notApplicable}</strong>
                            </div>
                            <div className="nd-compliance-categories-tooltip-row">
                              <span className="nd-compliance-categories-tooltip-left">
                                <span className="nd-risk-legend-dot nd-risk-legend-dot-non-complied" />
                                Non Complied
                              </span>
                              <strong>{item.nonComplied}</strong>
                            </div>
                            <div className="nd-compliance-categories-tooltip-total">
                              <span>Total</span>
                              <strong>{totalValue}</strong>
                            </div>
                          </div>
                          <p className="nd-compliance-categories-label">{item.label}</p>
                        </div>
                      );
                      })}
                    </div>
                  </div>
                </div>
              </article>
            </Col>
          </Row>
        </Container>
      </section>

      {/* <section className="lyt-section ">
      <Container fluid className="typ-container">
        <Row className="nd-bottom-row">
          <Col lg={9} md={12} className="nd-bottom-col">
            <article className="nd-data-card">
              <div className="nd-data-card-header">
                <p className="nd-data-card-title">ACTIVITY LOG</p>
                <button type="button" className="nd-link-btn nd-data-card-link">
                  View All
                </button>
              </div>

              <div className="table-responsive nd-activity-table-wrap">
                <table className="nd-activity-table">
                  <thead>
                    <tr>
                      <th>Sr.no</th>
                      <th>Company name</th>
                      <th>User Name</th>
                      <th>User Type</th>
                      <th>Date &amp; Time</th>
                      <th>Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityLogRows.map((item) => (
                      <tr key={item.id}>
                        <td>{item.srNo}</td>
                        <td>{item.companyName}</td>
                        <td>{item.userName}</td>
                        <td>{item.userType}</td>
                        <td>{item.dateTime}</td>
                        <td>
                          <span className="nd-activity-text" title={item.activity}>
                            {item.activity}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          </Col>

          <Col lg={3} md={12} className="nd-bottom-col">
            <article className="nd-data-card nd-user-details-card">
              <div className="nd-data-card-header">
                <p className="nd-data-card-title">USER DETAILS</p>
                <button type="button" className="nd-link-btn nd-data-card-link">
                  View All
                </button>
              </div>

              <div className="nd-user-total-block">
                <p className="nd-user-total-count">{totalUsersCount}</p>
                <p className="nd-user-total-label">Total Users</p>
              </div>

              <div className="nd-user-breakdown">
                {userDetailsList.map((item) => (
                  <div className="nd-user-breakdown-item" key={item.id}>
                    <div className="nd-user-breakdown-top">
                      <span>{item.label}</span>
                      <span>{item.value}</span>
                    </div>
                    <div className="nd-user-progress-track">
                      <span
                        className={`nd-user-progress-fill nd-user-progress-fill-${item.barClass}`}
                        style={{
                          width:
                            totalUsersCount > 0
                              ? `${(item.value / totalUsersCount) * 100}%`
                              : "0%",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </article>
          </Col>
        </Row>
        </Container>

      </section> */}


      <section className="lyt-section ">
        <Container fluid className="typ-container">
          <Row className="nd-bottom-row">
            <Col xs={12}>
              <article className="nd-data-card nd-company-overview-card">
                <div className="nd-data-card-header nd-company-overview-header">
                  <p className="nd-data-card-title">COMPANY OVERVIEW</p>
                  <button
                    type="button"
                    className="nd-link-btn nd-data-card-link"
                    onClick={() => navigate("/compliancer/compliencer-action")}
                  >
                    View All
                  </button>
                </div>

                <div className="table-responsive nd-company-overview-table-wrap">
                  <table className="nd-company-overview-table">
                    <thead>
                      <tr>
                        <th>Company Name</th>
                        <th>Total Branches</th>
                        <th>Total Categories</th>
                        <th>Total Dues</th>
                        <th>Total Compliances</th>
                        <th>Auditor Name</th>
                        <th>Approver Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyOverviewData.map((item, idx) => (
                        <tr key={item.company_name || idx}>
                          <td>{item.company_name}</td>
                          <td>{item.branch_count}</td>
                          <td>{item.category_count}</td>
                          <td>{item.total_dues} Dues</td>
                          <td>
                            <div className="nd-employee-stack">
                              <span className="nd-employee-more">
                                +{item.total_compliances}
                              </span>
                            </div>
                          </td>
                          <td>{item.auditor || "NA"}</td>
                          <td>{item.approver}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </article>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="lyt-section">
        <Container fluid className="typ-container" style={{ paddingBottom: "20px" }}>
          <Row className="nd-criticality-row">
            <Col lg={8} md={12}>
              <article className="nd-overview-card nd-criticality-card">
                <div className="nd-overview-header">
                  <p className="nd-overview-title">CRITICALITY RISK</p>
                  <select
                    className="nd-overview-filter"
                    value={criticalityRiskFilter || ""}
                    onChange={(e) => setCriticalityRiskFilter(e.target.value)}
                  >
                    <option value="" hidden>
                      Monthly
                    </option>
                    <option value={7}>Last seven days</option>
                    <option value={14}>Last two weeks</option>
                    <option value={21}>Last three weeks</option>
                    <option value={30}>Last one month</option>
                  </select>
                </div>

                <div className="table-responsive nd-criticality-table-wrap">
                  <table className="nd-criticality-table">
                    <thead>
                      <tr>
                        <th>Criticality</th>
                        <th>Complied</th>
                        <th>Partial Complied</th>
                        <th>Not Applicable</th>
                        <th>Not Complied</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {criticalityRiskRows.map((item) => (
                        <tr key={item.id} className={item.isTotal ? "nd-criticality-total-row" : ""}>
                          <td>{item.criticality}</td>
                          <td>{item.complied}</td>
                          <td>{item.partialComplied}</td>
                          <td>{item.notApplicable}</td>
                          <td>{item.notComplied}</td>
                          <td>{item.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </article>
            </Col>

            <Col lg={4} md={12}>
              <article className="nd-overview-card nd-risk-statistics-card">
                <div className="nd-overview-header nd-risk-statistics-header">
                  <p className="nd-overview-title">RISK STATISTICS</p>
                  <div className="nd-risk-statistics-actions">
                    <p className="nd-risk-total-label">
                      Total: <span>{totalRiskStatistics}</span>
                    </p>
                    <select
                      className="nd-overview-filter"
                      value={statisticsRiskFilter || ""}
                      onChange={(e) => setStaticticsRiskFilter(e.target.value)}
                    >
                      <option value="" hidden>
                        Monthly
                      </option>
                      <option value={7}>Last seven days</option>
                      <option value={14}>Last two weeks</option>
                      <option value={21}>Last three weeks</option>
                      <option value={30}>Last one month</option>
                    </select>
                  </div>
                </div>

                <div className="nd-risk-statistics-legend">
                  {riskStatisticsLegend.map((item) => (
                    <span className="nd-risk-legend-item" key={item.id}>
                      <span className={`nd-risk-legend-dot nd-risk-legend-dot-${item.colorClass}`} />
                      {item.label}
                    </span>
                  ))}
                </div>

                <div className="nd-risk-chart-grid">
                  <div className="nd-risk-chart-y-axis">
                    {riskStatisticsYAxis.map((value) => (
                      <span className="nd-risk-chart-y-tick" key={value}>
                        {value}
                      </span>
                    ))}
                  </div>

                  <div className="nd-risk-chart-body">
                    {riskStatisticsYAxis.map((value) => (
                      <span className="nd-risk-chart-line" key={value} />
                    ))}

                    <div className="nd-risk-chart-groups">
                      {riskStatisticsSeries.map((item) => (
                        <div className="nd-risk-chart-group" key={item.id}>
                          <div className="nd-risk-chart-bars">
                            <span
                              className="nd-risk-bar nd-risk-bar-complied"
                              style={{ height: `${(item.complied / riskStatisticsMax) * 100}%` }}
                            />
                            <span
                              className="nd-risk-bar nd-risk-bar-partial"
                              style={{ height: `${(item.partial / riskStatisticsMax) * 100}%` }}
                            />
                            <span
                              className="nd-risk-bar nd-risk-bar-not-applicable"
                              style={{ height: `${(item.notApplicable / riskStatisticsMax) * 100}%` }}
                            />
                            <span
                              className="nd-risk-bar nd-risk-bar-non-complied"
                              style={{ height: `${(item.nonComplied / riskStatisticsMax) * 100}%` }}
                            />
                          </div>
                          <p className="nd-risk-chart-label">{item.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            </Col>
          </Row>
        </Container>
      </section>


    

    </main>
  );
};

export default NewDashbord;